/**
 * Category Growth Chart Component
 *
 * Visualizes period-over-period category growth using lollipop or bar charts.
 * Shows revenue or project count growth rates with positive/negative indicators.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  ComposedChart,
  Scatter,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import type { CategoryRevenue } from "@/shared/types/sales-analytics";

interface CategoryGrowthChartProps {
  data: CategoryRevenue[];
  chartType: 'lollipop' | 'bar';
  metricType: 'revenue' | 'projectCount';
  title?: string;
  description?: string;
  topN?: number; // Show top N categories by growth rate
}

interface ChartDataPoint {
  categoryName: string;
  category: string;
  growthPercentage: number;
  currentValue: number;
  previousValue: number;
  changeAmount: number;
}

/**
 * Custom label renderer for percentage at end of lollipop
 */
const LollipopLabel = (props: any) => {
  const { x, y, width, height, value } = props;

  // Position label at the end of the bar
  const labelX = width >= 0 ? x + width + 15 : x + width - 15;
  const labelY = y + height / 2;

  // Text anchor based on positive/negative
  const anchor = width >= 0 ? 'start' : 'end';

  return (
    <text
      x={labelX}
      y={labelY}
      fill="#000"
      textAnchor={anchor}
      dominantBaseline="middle"
      fontSize={14}
      fontWeight={600}
    >
      {value >= 0 ? '+' : ''}{value.toFixed(1)}%
    </text>
  );
};

/**
 * Custom tooltip for growth charts
 */
const CustomGrowthTooltip = ({ active, payload, metricType }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as ChartDataPoint;
    const isRevenue = metricType === 'revenue';

    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <p className="font-semibold mb-2">{data.categoryName}</p>
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">
            Current: {isRevenue ? `฿${data.currentValue.toLocaleString()}` : data.currentValue}
          </p>
          <p className="text-muted-foreground">
            Previous: {isRevenue ? `฿${data.previousValue.toLocaleString()}` : data.previousValue}
          </p>
          <p className={`font-semibold ${data.growthPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            Growth: {data.growthPercentage >= 0 ? '+' : ''}
            {data.growthPercentage.toFixed(1)}%
          </p>
          <p className="text-xs text-muted-foreground">
            Change: {data.changeAmount >= 0 ? '+' : ''}
            {isRevenue ? `฿${data.changeAmount.toLocaleString()}` : data.changeAmount}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export function CategoryGrowthChart({
  data,
  chartType,
  metricType,
  title = "Category Growth",
  description,
  topN = 10
}: CategoryGrowthChartProps) {
  // Filter categories with growth data
  const categoriesWithGrowth = data.filter(cat => cat.growth);

  if (categoriesWithGrowth.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            No growth data available. Enable period comparison to see growth metrics.
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData: ChartDataPoint[] = categoriesWithGrowth
    .map(cat => {
      const growthData = metricType === 'revenue' ? cat.growth!.revenue : cat.growth!.projectCount;
      return {
        categoryName: cat.categoryName,
        category: cat.category,
        growthPercentage: growthData.changePercentage,
        currentValue: metricType === 'revenue' ? cat.revenue : cat.projectCount,
        previousValue: growthData.previousValue,
        changeAmount: growthData.changeAmount
      };
    })
    .sort((a, b) => b.growthPercentage - a.growthPercentage)
    .slice(0, topN);

  // Render lollipop chart
  if (chartType === 'lollipop') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={chartData}
              layout="vertical"
              margin={{ top: 20, right: 30, bottom: 20, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                type="number"
                domain={['auto', 'auto']}
                tickFormatter={(value) => `${value}%`}
              />
              <YAxis
                type="category"
                dataKey="categoryName"
                width={90}
                tick={{ fontSize: 12 }}
              />
              <ReferenceLine x={0} stroke="#666" strokeWidth={2} />
              <Tooltip content={<CustomGrowthTooltip metricType={metricType} />} />

              {/* Thin bar as line - thicker for visibility */}
              <Bar
                dataKey="growthPercentage"
                barSize={5}
                label={<LollipopLabel />}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.growthPercentage >= 0 ? '#22c55e' : '#ef4444'}
                  />
                ))}
              </Bar>

              {/* Circle at the end - larger for emphasis */}
              <Scatter
                dataKey="growthPercentage"
                fill="#1e293b"
                r={9}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  }

  // Render horizontal bar chart
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, bottom: 20, left: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              type="number"
              domain={['auto', 'auto']}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis
              type="category"
              dataKey="categoryName"
              width={90}
              tick={{ fontSize: 12 }}
            />
            <ReferenceLine x={0} stroke="#666" strokeWidth={2} />
            <Tooltip content={<CustomGrowthTooltip metricType={metricType} />} />
            <Bar dataKey="growthPercentage" radius={[0, 4, 4, 0]} barSize={30}>
              {chartData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.growthPercentage >= 0 ? '#22c55e' : '#ef4444'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
